// components/NewInpaintToolbar.tsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  Brush,
  Circle,
  Diamond,
  Eraser,
  Hexagon,
  LucideIcon,
  MessageCircle,
  Minus,
  MousePointer2,
  Pencil,
  Pentagon,
  PenTool,
  Scissors,
  Shapes,
  Square,
  SquareDashed,
  Star,
  Triangle,
  Type,
} from '@/components/ui/lucide-memo';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { DrawingToolKind, InpaintTool, MaskPenMode } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import { setProjectRightSidebarTab, setProjectSelectedLayersId } from '#lib/project-runtime-layer-ui-state';
import {
  findShortcutChordForCommand,
  formatShortcutChordForDisplay,
  isInertSurface,
  shouldIgnoreKeyboardShortcutEvent,
  STUDIO_TOOL_SHORTCUTS,
} from '#lib/keyboard-shortcuts';

const ANNOTATION_TOOLS: InpaintTool[] = ['annotationLine', 'annotationEraser', 'comment'];
const ANNOTATION_COLOR_SWATCHES = [
  { id: 'red', label: 'Red', value: '#FF3B30', shortcut: '1' },
  { id: 'green', label: 'Neon Green', value: '#39FF14', shortcut: '2' },
  { id: 'yellow', label: 'Yellow', value: '#FFE066', shortcut: '3' },
  { id: 'cyan', label: 'Cyan', value: '#00F0FF', shortcut: '4' },
  { id: 'white', label: 'White', value: '#FFFFFF', shortcut: '5' },
  { id: 'black', label: 'Black', value: '#000000', shortcut: '6' },
];
const DRAWING_TOOLS: Array<{ kind: DrawingToolKind; label: string; icon: IconType }> = [
  { kind: 'rectangle', label: 'Rectangle', icon: Square },
  { kind: 'ellipse', label: 'Ellipse', icon: Circle },
  { kind: 'triangle', label: 'Triangle', icon: Triangle },
  { kind: 'diamond', label: 'Diamond', icon: Diamond },
  { kind: 'star', label: 'Star', icon: Star },
  { kind: 'pentagon', label: 'Pentagon', icon: Pentagon },
  { kind: 'hexagon', label: 'Hexagon', icon: Hexagon },
  { kind: 'polygon', label: 'Polygon', icon: Shapes },
  { kind: 'line', label: 'Line', icon: Minus },
  { kind: 'arrow', label: 'Arrow', icon: ArrowRight },
  { kind: 'pencil', label: 'Pencil', icon: Pencil },
  { kind: 'text', label: 'Text', icon: Type },
];
const HEXAGON_DIMENSIONS = { width: 34, height: 30 };
const MASK_PEN_MODES: Array<{ mode: MaskPenMode; label: string; icon: IconType }> = [
  { mode: 'pen', label: 'Pen', icon: PenTool },
  { mode: 'rect', label: 'Rectangle', icon: Square },
  { mode: 'brush', label: 'Brush', icon: Brush },
];
const HONEYCOMB_COLUMNS = [
  ANNOTATION_COLOR_SWATCHES.filter((_, index) => index % 2 === 0),
  ANNOTATION_COLOR_SWATCHES.filter((_, index) => index % 2 === 1),
];
const HONEYCOMB_COLUMN_STYLES: React.CSSProperties[] = [
  { marginTop: 0 },
  { marginTop: HEXAGON_DIMENSIONS.height / 2 },
];
const TOOLBAR_BUTTON_BASE_STYLE: React.CSSProperties = {
  borderRadius: '9999px', cursor: 'pointer', width: 36, height: 36,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const DESKTOP_TOOLBAR_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '8px',
  transform: 'translateY(-50%)',
  // Above the workspace name tags (20) and frame overlays (40) that pan beneath it.
  zIndex: 45,
};
const SIZE_TRACK_TICKS = Array.from({ length: 5 }, (_, index) => (
  <span key={`tick-${index}`} className="block h-px w-4 bg-white/15" />
));

type IconType = LucideIcon | React.ComponentType<{ color?: string; size?: number; className?: string }>;

// Custom "pencil-sparkles" glyph for the Annotation Pen (lucide-style: currentColor stroke + size).
const PencilSparkles: React.FC<{ color?: string; size?: number; className?: string }> = ({
  color = 'currentColor',
  size = 24,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M10 3H8" />
    <path d="m15.007 5.008 3.987 3.986" />
    <path d="M20 15v4" />
    <path d="M21.174 6.813a2.82 2.82 0 0 0-3.986-3.987L3.842 16.175a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    <path d="M22 17h-4" />
    <path d="M4 5v4" />
    <path d="M6 7H2" />
    <path d="M9 2v2" />
  </svg>
);

interface ToolbarButtonProps {
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  onLongPress?: (target: HTMLElement) => void;
  longPressDelayMs?: number;
  buttonRef?: React.RefObject<HTMLDivElement | null>;
  badge?: React.ReactNode;
  tooltipText: string;
  icon: IconType;
  selected?: boolean;
  tooltipSide?: 'top' | 'left' | 'right' | 'bottom';
}

const ToolbarButton: React.FC<ToolbarButtonProps> = React.memo(({
  onClick,
  onContextMenu,
  onLongPress,
  longPressDelayMs = 450,
  buttonRef,
  badge,
  tooltipText,
  icon: Icon,
  selected = false,
  tooltipSide = 'left'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onClick(event);
  }, [onClick]);
  const stopPointerPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  let iconColor: string;
  let currentBackgroundColor: string | undefined;

  if (selected) {
    iconColor = '#0D0D0D';
    currentBackgroundColor = '#ECECEC';
  } else if (isHovered) {
    iconColor = '#FFFFFF';
    currentBackgroundColor = '#2A2A2A';
  } else {
    iconColor = '#B4B4B4';
    currentBackgroundColor = 'transparent';
  }

  const buttonStyle = useMemo(
    () => ({ ...TOOLBAR_BUTTON_BASE_STYLE, backgroundColor: currentBackgroundColor }),
    [currentBackgroundColor],
  );

  return (<Tooltip><TooltipTrigger asChild>
    <div
      ref={buttonRef}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onMouseDown={stopPointerPropagation}
      onTouchStart={stopPointerPropagation}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative transition-colors duration-150 ease-in-out"
      style={buttonStyle}
    >
      <Icon color={iconColor} size={18} />
      {badge}
    </div>
  </TooltipTrigger><TooltipContent side={tooltipSide} sideOffset={8}>{tooltipText}</TooltipContent></Tooltip>);
});
ToolbarButton.displayName = 'ToolbarButton';

// Shared row for the two slim drawers (mask modes, drawing shapes): same icon + label + active
// shape. `value` is the identifier handed back to the stable parent callback, so no closure is
// created per item.
interface DrawerMenuItemProps {
  value: string;
  label: string;
  icon: IconType;
  isActive: boolean;
  onSelect: (value: string) => void;
  onCanvasInteraction: (event: React.SyntheticEvent) => void;
}

const DrawerMenuItem: React.FC<DrawerMenuItemProps> = React.memo(({
  value,
  label,
  icon: Icon,
  isActive,
  onSelect,
  onCanvasInteraction,
}) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect(value);
  }, [onSelect, value]);

  return (
    <button
      type="button"
      onMouseDown={onCanvasInteraction}
      onTouchStart={onCanvasInteraction}
      onClick={handleClick}
      className={`flex items-center gap-2.5 rounded-[6px] px-2.5 py-2 text-left text-sm transition-colors ${
        isActive ? 'bg-white/10 text-white' : 'text-neutral-300 hover:bg-white/[0.06] hover:text-white'
      }`}
    >
      <Icon size={16} color={isActive ? '#ffffff' : '#a3a3a3'} />
      <span>{label}</span>
    </button>
  );
});
DrawerMenuItem.displayName = 'DrawerMenuItem';

interface ColorSwatchButtonProps {
  swatch: (typeof ANNOTATION_COLOR_SWATCHES)[number];
  isActive: boolean;
  onSelect: (value: string) => void;
  onCanvasInteraction: (event: React.SyntheticEvent) => void;
}

const ColorSwatchButton: React.FC<ColorSwatchButtonProps> = React.memo(({
  swatch,
  isActive,
  onSelect,
  onCanvasInteraction,
}) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect(swatch.value);
  }, [onSelect, swatch.value]);

  const style = useMemo<React.CSSProperties>(() => ({
    width: HEXAGON_DIMENSIONS.width,
    height: HEXAGON_DIMENSIONS.height,
    clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
    backgroundColor: swatch.value,
    boxShadow: isActive
      ? `0 0 18px ${swatch.value}88, 0 4px 12px rgba(0,0,0,0.55), inset 0 0 1px rgba(255,255,255,0.65)`
      : '0 6px 12px rgba(0,0,0,0.5)',
    filter: isActive ? 'brightness(1.06)' : 'brightness(0.94)',
  }), [swatch.value, isActive]);

  return (
    <button
      type="button"
      aria-label={`Select ${swatch.label}`}
      onMouseDown={onCanvasInteraction}
      onTouchStart={onCanvasInteraction}
      onClick={handleClick}
      className="group relative transition-all duration-200 ease-out hover:scale-110 active:scale-95"
      style={style}
    >
      <span className="absolute inset-0 rounded-[12px] opacity-0 group-hover:opacity-80 transition-opacity mix-blend-screen text-[8px] font-semibold text-white flex items-center justify-center">
        {swatch.shortcut}
      </span>
    </button>
  );
});
ColorSwatchButton.displayName = 'ColorSwatchButton';

interface NewInpaintToolbarProps {
  projectId: string;
  openDrawingPanelRequestToken?: number;
}

const NewInpaintToolbar: React.FC<NewInpaintToolbarProps> = React.memo(({
  projectId,
  openDrawingPanelRequestToken,
}) => {
  const { kernel } = useKernel();

  const annotationBrushSize = useKernelStore(state => state.annotationBrushSize);
  const annotationColor = useKernelStore(state => state.annotationColor);
  const activeTool = useKernelStore(state => state.activeTool);
  const maskPenMode = useKernelStore(state => state.maskPenMode);
  const drawingToolKind = useKernelStore(state => state.drawingToolKind);
  const keyboardShortcuts = useKernelStore(state => state.userPreferences.shortcuts);
  const showAllTools = useKernelStore(state => state.userPreferences.studioToolbarTools === 'ALL');
  // A Vector project edits objects, not pixels: shapes are essential, the pixel cut and mask are not offered.
  const isVectorProject = useKernelStore(state => state.inventory.Project.registry[projectId]?.projectType === 'VECTOR');
  const showPixelTools = !isVectorProject;
  const showDrawTool = showAllTools || isVectorProject;
  const activeModal = useKernelStore(state => state.activeModal);
  const hasVideoTimelineModeInWorkspace = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const workspaceId = typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
    const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
    if (!workspace || workspace.preferVersionPanelOverTimeline === true) return false;
    const layerIds = Array.isArray(workspace.layerIds) ? workspace.layerIds : [];
    return layerIds.some((layerId) => {
      const layer = state.inventory.StudioLayer.registry[layerId];
      if (!layer || layer.visible === false || (layer.type !== 'video' && layer.type !== 'audio')) return false;
      const src = typeof layer.videoSrc === 'string' ? layer.videoSrc.trim() : '';
      return src.length > 0;
    });
  });

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const drawerRef = useRef<HTMLDivElement | null>(null);
  const annotationButtonRef = useRef<HTMLDivElement | null>(null);
  const commentButtonRef = useRef<HTMLDivElement | null>(null);
  const annotationEraserButtonRef = useRef<HTMLDivElement | null>(null);
  const drawingButtonRef = useRef<HTMLDivElement | null>(null);
  const maskPenButtonRef = useRef<HTMLDivElement | null>(null);
  const sizeTrackRef = useRef<HTMLDivElement | null>(null);
  const sizePointerIdRef = useRef<number | null>(null);
  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    kind: 'annotation' | 'drawing' | 'maskPen';
    anchor: { x: number; y: number; mode?: 'desktop' | 'mobile' } | null;
  }>({
    open: false,
    kind: 'annotation',
    anchor: null,
  });

  const handleSetActiveTool = useCallback((tool: InpaintTool) => {
    if (tool !== kernel.current?.getActiveTool()) {
      kernel.current?.logUserAction('Inpaint Tool Selected', { tool_name: tool });
    }
    kernel.current?.setActiveTool(tool);
  }, [kernel]);
  // The hidden pixel tools stay reachable by shortcut or carried over from a Canvas project.
  useEffect(() => {
    if (isVectorProject && (activeTool === 'scissors' || activeTool === 'maskPen')) kernel.current?.setActiveTool(null);
  }, [kernel, isVectorProject, activeTool]);

  // Reveal the unified properties (settings) panel in the right sidebar. Tools whose properties live
  // there (Draw, Mask Pen) switch the sidebar to the settings tab when activated.
  const openLayerSettingsPanel = useCallback(() => {
    if (!kernel.current || !projectId) return;
    setProjectRightSidebarTab(kernel.current, projectId, 'settings');
    kernel.current.openRightPanel();
  }, [kernel, projectId]);

  const enterDrawingDefaultsMode = useCallback(() => {
    if (!kernel.current || !projectId) return;
    setProjectSelectedLayersId(kernel.current, projectId, []);
    openLayerSettingsPanel();
  }, [kernel, openLayerSettingsPanel, projectId]);

  const stopCanvasInteractionPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerState((current) => ({ open: false, kind: current.kind, anchor: null }));
  }, []);

  const openDrawerAtTarget = useCallback((target: HTMLElement | null, kind: 'annotation' | 'drawing' | 'maskPen' = 'annotation') => {
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const isMobileView = window.innerWidth < 768;

    if (isMobileView) {
      setDrawerState({
        open: true,
        kind,
        anchor: {
          x: rect.left + rect.width / 2,
          y: rect.bottom + 8, // Position below the button
          mode: 'mobile'
        },
      });
    } else {
      setDrawerState({
        open: true,
        kind,
        anchor: {
          x: rect.right + 16,
          y: rect.top + rect.height / 2,
          mode: 'desktop'
        },
      });
    }
  }, []);

  const handleAnnotationDrawerContext = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    openDrawerAtTarget(event.currentTarget);
    kernel.current?.logUserAction('Annotation Drawer Opened', { source: 'context_menu' });
  }, [kernel, openDrawerAtTarget]);

  const handleAnnotationDrawerLongPress = useCallback((target: HTMLElement) => {
    openDrawerAtTarget(target);
    kernel.current?.logUserAction('Annotation Drawer Opened', { source: 'long_press' });
  }, [kernel, openDrawerAtTarget]);

  useEffect(() => {
    if (!drawerState.open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        closeDrawer();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerState.open, closeDrawer]);

  const handleDeselectToolClick = useCallback((event: React.MouseEvent) => { event.stopPropagation(); handleSetActiveTool(null); }, [handleSetActiveTool]);
  const handleCanvasControlToolClick = useCallback((event: React.MouseEvent) => { event.stopPropagation(); handleSetActiveTool('canvasControl'); }, [handleSetActiveTool]);
  const handleScissorsToolClick = useCallback((event: React.MouseEvent) => { event.stopPropagation(); handleSetActiveTool('scissors'); }, [handleSetActiveTool]);
  const handleMaskPenToolClick = useCallback((event: React.MouseEvent) => { event.stopPropagation(); handleSetActiveTool('maskPen'); openLayerSettingsPanel(); }, [handleSetActiveTool, openLayerSettingsPanel]);
  const handleMaskPenContext = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (kernel.current?.getActiveTool() !== 'maskPen') handleSetActiveTool('maskPen');
    openLayerSettingsPanel();
    openDrawerAtTarget(maskPenButtonRef.current ?? (event.currentTarget as HTMLElement), 'maskPen');
    kernel.current?.logUserAction('Mask Pen Drawer Opened', { source: 'context_menu' });
  }, [handleSetActiveTool, openLayerSettingsPanel, openDrawerAtTarget, kernel]);
  const handleDrawingToolClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.getActiveTool() !== 'draw') {
      handleSetActiveTool('draw');
    }
    enterDrawingDefaultsMode();
    openDrawerAtTarget(drawingButtonRef.current ?? (event.currentTarget as HTMLElement), 'drawing');
    kernel.current?.logUserAction('Drawing Shape Picker Opened', { source: 'button' });
  }, [handleSetActiveTool, enterDrawingDefaultsMode, openDrawerAtTarget, kernel]);

  useEffect(() => {
    if (!openDrawingPanelRequestToken) return;
    openLayerSettingsPanel();
  }, [openDrawingPanelRequestToken, openLayerSettingsPanel]);

  const handleDrawingDrawerContext = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (kernel.current?.getActiveTool() !== 'draw') handleSetActiveTool('draw');
    enterDrawingDefaultsMode();
    openDrawerAtTarget(drawingButtonRef.current ?? (event.currentTarget as HTMLElement), 'drawing');
    kernel.current?.logUserAction('Drawing Shape Picker Opened', { source: 'context_menu' });
  }, [handleSetActiveTool, enterDrawingDefaultsMode, kernel, openDrawerAtTarget]);
  const handleDrawingDrawerLongPress = useCallback((target: HTMLElement) => {
    if (kernel.current?.getActiveTool() !== 'draw') handleSetActiveTool('draw');
    enterDrawingDefaultsMode();
    openDrawerAtTarget(target, 'drawing');
    kernel.current?.logUserAction('Drawing Drawer Opened', { source: 'long_press' });
  }, [enterDrawingDefaultsMode, handleSetActiveTool, kernel, openDrawerAtTarget]);

  const handleAnnotationLineToolClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.getActiveTool() === 'annotationLine') {
      openDrawerAtTarget(annotationButtonRef.current);
    } else {
      handleSetActiveTool('annotationLine');
    }
  }, [kernel, openDrawerAtTarget, handleSetActiveTool]);
  const handleAnnotationEraserToolClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.getActiveTool() === 'annotationEraser') {
      openDrawerAtTarget(annotationEraserButtonRef.current);
    } else {
      handleSetActiveTool('annotationEraser');
    }
  }, [kernel, openDrawerAtTarget, handleSetActiveTool]);
  const handleCommentToolClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (kernel.current?.getActiveTool() === 'comment') {
      openDrawerAtTarget(commentButtonRef.current);
    } else {
      handleSetActiveTool('comment');
    }
  }, [kernel, openDrawerAtTarget, handleSetActiveTool]);

  const handleAnnotationSizeChange = useCallback((value: number, source: 'slider' | 'shortcut') => {
    const clamped = Math.max(1, Math.min(96, value));
    if (clamped === kernel.current?.getAnnotationBrushSize()) return;
    kernel.current?.setAnnotationBrushSize(clamped);
    kernel.current?.logUserAction('Annotation Tool Size Changed', { new_size: clamped, source });
  }, [kernel]);

  const handleAnnotationColorChange = useCallback((color: string, source: 'swatch' | 'shortcut') => {
    if (color.toLowerCase() === kernel.current?.getAnnotationColor().toLowerCase()) {
      if (source === 'swatch') {
        closeDrawer();
      }
      return;
    }
    kernel.current?.setAnnotationColor(color);
    kernel.current?.logUserAction('Annotation Color Changed', { color, source });
    if (source === 'swatch') {
      closeDrawer();
    }
  }, [kernel, closeDrawer]);

  const updateSizeFromPointer = useCallback((clientY: number) => {
    if (!sizeTrackRef.current) return;
    const rect = sizeTrackRef.current.getBoundingClientRect();
    const relative = 1 - (clientY - rect.top) / rect.height;
    const clampedRelative = Math.min(1, Math.max(0, relative));
    const value = Math.round(clampedRelative * 95) + 1;
    handleAnnotationSizeChange(value, 'slider');
  }, [handleAnnotationSizeChange]);

  const handleSizePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    sizePointerIdRef.current = event.pointerId;
    sizeTrackRef.current?.setPointerCapture?.(event.pointerId);
    updateSizeFromPointer(event.clientY);
  }, [updateSizeFromPointer]);

  const handleSizePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (sizePointerIdRef.current !== event.pointerId) return;
    updateSizeFromPointer(event.clientY);
  }, [updateSizeFromPointer]);

  const handleSizePointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (sizePointerIdRef.current !== event.pointerId) return;
    sizeTrackRef.current?.releasePointerCapture?.(event.pointerId);
    sizePointerIdRef.current = null;
    updateSizeFromPointer(event.clientY);
  }, [updateSizeFromPointer]);

  const handleSizePointerCancel = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (sizePointerIdRef.current !== event.pointerId) return;
    sizeTrackRef.current?.releasePointerCapture?.(event.pointerId);
    sizePointerIdRef.current = null;
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!ANNOTATION_TOOLS.includes(activeTool)) return;
      if (shouldIgnoreKeyboardShortcutEvent(event, activeModal) || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      const numeric = Number(event.key);
      if (!Number.isInteger(numeric)) return;
      const swatch = ANNOTATION_COLOR_SWATCHES[numeric - 1];
      if (!swatch) return;
      event.preventDefault();
      handleAnnotationColorChange(swatch.value, 'shortcut');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeModal, activeTool, handleAnnotationColorChange]);

  useEffect(() => {
    if (activeTool !== 'sound') return;
    kernel.current?.setActiveTool(null);
  }, [activeTool, kernel]);

  const drawerPosition = useMemo(() => {
    if (!drawerState.anchor) return null;
    if (drawerState.anchor.mode === 'mobile') {
      return {
        left: drawerState.anchor.x - 44, // Centered (88px / 2)
        top: drawerState.anchor.y, // Position below anchor
      };
    }
    // Slim popups (the shape picker and mask-mode menu) are short, so sit them next to the button
    // instead of using the tall-drawer upward shift — they line up with the icon you clicked.
    const verticalShift = drawerState.kind === 'drawing' ? 126 : drawerState.kind === 'maskPen' ? 70 : 120;
    return {
      left: drawerState.anchor.x,
      top: Math.max(16, drawerState.anchor.y - verticalShift),
    };
  }, [drawerState.anchor, drawerState.kind]);

  const shortcutByTool = useMemo(() => {
    const result = new Map<string | null, string>();
    for (const shortcut of STUDIO_TOOL_SHORTCUTS) {
      const chord = findShortcutChordForCommand(keyboardShortcuts, shortcut.command);
      if (chord) result.set(shortcut.tool, formatShortcutChordForDisplay(chord));
    }
    return result;
  }, [keyboardShortcuts]);

  // Declared above renderTools: the memoised renderTools closes over them at definition time, so
  // keeping them below would put them in the temporal dead zone.
  const activeDrawingToolIcon = useMemo(() => DRAWING_TOOLS.find((tool) => tool.kind === drawingToolKind)?.icon ?? Shapes, [drawingToolKind]);
  const activeMaskPenIcon = useMemo(() => (maskPenMode === 'brush' ? Brush : maskPenMode === 'rect' ? Square : PenTool), [maskPenMode]);

  const withShortcut = useCallback((label: string, tool: InpaintTool): string => {
    const shortcut = shortcutByTool.get(tool);
    return shortcut ? `${label} · ${shortcut}` : label;
  }, [shortcutByTool]);

  const renderTools = useCallback((tooltipSide: 'right' | 'top' | 'bottom') => (
    <>
      <ToolbarButton onClick={handleDeselectToolClick} tooltipText={withShortcut('Select', null)} icon={MousePointer2} selected={activeTool === null} tooltipSide={tooltipSide} />
      <ToolbarButton onClick={handleCanvasControlToolClick} tooltipText={withShortcut('Generation area', 'canvasControl')} icon={SquareDashed} selected={activeTool === 'canvasControl'} tooltipSide={tooltipSide} />
      {showPixelTools && (
        <ToolbarButton
          onClick={handleScissorsToolClick}
          tooltipText={withShortcut(hasVideoTimelineModeInWorkspace ? 'Split track' : 'Cut layer', 'scissors')}
          icon={Scissors}
          selected={activeTool === 'scissors'}
          tooltipSide={tooltipSide}
        />
      )}
      <ToolbarButton
        onClick={handleAnnotationLineToolClick}
        onContextMenu={handleAnnotationDrawerContext}
        onLongPress={handleAnnotationDrawerLongPress}
        tooltipText={withShortcut('Annotation pen', 'annotationLine')}
        icon={PencilSparkles}
        selected={activeTool === 'annotationLine'}
        tooltipSide={tooltipSide}
        buttonRef={annotationButtonRef}
      />
      <ToolbarButton
        onClick={handleAnnotationEraserToolClick}
        onContextMenu={handleAnnotationDrawerContext}
        onLongPress={handleAnnotationDrawerLongPress}
        tooltipText={withShortcut('Annotation eraser', 'annotationEraser')}
        icon={Eraser}
        selected={activeTool === 'annotationEraser'}
        tooltipSide={tooltipSide}
        buttonRef={annotationEraserButtonRef}
      />
      {showDrawTool && (
        <>
          {showAllTools && (
            <ToolbarButton
              onClick={handleCommentToolClick}
              onContextMenu={handleAnnotationDrawerContext}
              onLongPress={handleAnnotationDrawerLongPress}
              tooltipText={withShortcut('Comment', 'comment')}
              icon={MessageCircle}
              selected={activeTool === 'comment'}
              tooltipSide={tooltipSide}
              buttonRef={commentButtonRef}
            />
          )}
          <ToolbarButton
            onClick={handleDrawingToolClick}
            onContextMenu={handleDrawingDrawerContext}
            onLongPress={handleDrawingDrawerLongPress}
            tooltipText={withShortcut('Shapes', 'draw')}
            icon={activeDrawingToolIcon}
            selected={activeTool === 'draw'}
            tooltipSide={tooltipSide}
            buttonRef={drawingButtonRef}
          />
          {showPixelTools && (
            <ToolbarButton
              onClick={handleMaskPenToolClick}
              onContextMenu={handleMaskPenContext}
              tooltipText={withShortcut('Mask', 'maskPen')}
              icon={activeMaskPenIcon}
              selected={activeTool === 'maskPen'}
              tooltipSide={tooltipSide}
              buttonRef={maskPenButtonRef}
            />
          )}
        </>
      )}
    </>
  ), [
    activeTool,
    activeDrawingToolIcon,
    activeMaskPenIcon,
    hasVideoTimelineModeInWorkspace,
    showAllTools,
    showDrawTool,
    showPixelTools,
    withShortcut,
    handleDeselectToolClick,
    handleCanvasControlToolClick,
    handleScissorsToolClick,
    handleAnnotationLineToolClick,
    handleCommentToolClick,
    handleAnnotationEraserToolClick,
    handleAnnotationDrawerContext,
    handleAnnotationDrawerLongPress,
    handleDrawingToolClick,
    handleDrawingDrawerContext,
    handleDrawingDrawerLongPress,
    handleMaskPenToolClick,
    handleMaskPenContext,
  ]);

  const handleColorSwatchSelect = useCallback((value: string) => {
    handleAnnotationColorChange(value, 'swatch');
  }, [handleAnnotationColorChange]);

  const handleMaskPenModeSelect = useCallback((value: string) => {
    const mode = value as MaskPenMode;
    kernel.current?.setMaskPenMode(mode);
    kernel.current?.logUserAction('Mask Pen Mode Selected', { mode });
    closeDrawer();
  }, [kernel, closeDrawer]);

  const colorPicker = useMemo(() => (
    <div className="flex justify-center gap-0">
      {HONEYCOMB_COLUMNS.map((column, columnIndex) => (
        <div
          key={`column-${columnIndex}`}
          className="flex flex-col gap-2"
          style={HONEYCOMB_COLUMN_STYLES[columnIndex]}
        >
          {column.map((swatch) => (
            <ColorSwatchButton
              key={swatch.id}
              swatch={swatch}
              isActive={swatch.value.toLowerCase() === annotationColor.toLowerCase()}
              onSelect={handleColorSwatchSelect}
              onCanvasInteraction={stopCanvasInteractionPropagation}
            />
          ))}
        </div>
      ))}
    </div>
  ), [annotationColor, handleColorSwatchSelect, stopCanvasInteractionPropagation]);

  const maskPenDrawer = useMemo(() => (
    <div className="flex flex-col gap-1 px-1">
      {MASK_PEN_MODES.map(({ mode, label, icon }) => (
        <DrawerMenuItem
          key={mode}
          value={mode}
          label={label}
          icon={icon}
          isActive={maskPenMode === mode}
          onSelect={handleMaskPenModeSelect}
          onCanvasInteraction={stopCanvasInteractionPropagation}
        />
      ))}
    </div>
  ), [maskPenMode, handleMaskPenModeSelect, stopCanvasInteractionPropagation]);

  const handleDrawingKindSelect = useCallback((value: string) => {
    const kind = value as DrawingToolKind;
    if (kernel.current?.getActiveTool() !== 'draw') {
      handleSetActiveTool('draw');
    }
    openLayerSettingsPanel();
    kernel.current?.setDrawingToolKind(kind);
    kernel.current?.logUserAction('Drawing Shape Selected', { kind });
    closeDrawer();
  }, [handleSetActiveTool, openLayerSettingsPanel, kernel, closeDrawer]);

  const drawingDrawer = useMemo(() => (
    <div className="flex flex-col gap-1 px-1">
      {DRAWING_TOOLS.map(({ kind, label, icon }) => (
        <DrawerMenuItem
          key={kind}
          value={kind}
          label={label}
          icon={icon}
          isActive={kind === drawingToolKind}
          onSelect={handleDrawingKindSelect}
          onCanvasInteraction={stopCanvasInteractionPropagation}
        />
      ))}
    </div>
  ), [drawingToolKind, handleDrawingKindSelect, stopCanvasInteractionPropagation]);

  return (
    <>
      {isMobile ? (
        /* Mobile Layout: Horizontal, Top, Centered */
        <div 
          className="absolute top-1 left-0 w-full flex justify-center pointer-events-none z-20"
          id="editor-canvas-toolbar"
          data-drawing-controls="true"
          onMouseDown={stopCanvasInteractionPropagation}
          onTouchStart={stopCanvasInteractionPropagation}
        >
          <div className="pointer-events-auto flex flex-col items-center gap-1 rounded-full border border-[#262626] bg-[#1B1B1B] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-0.5">
              {renderTools('bottom')}
            </div>
          </div>
        </div>
      ) : (
        /* Desktop Layout: Vertical, Left, Centered */
        <div
          className="pointer-events-auto flex flex-col gap-0.5 rounded-full border border-[#262626] bg-[#1B1B1B] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
          id="editor-canvas-toolbar"
          data-drawing-controls="true"
          style={DESKTOP_TOOLBAR_STYLE}
          onMouseDown={stopCanvasInteractionPropagation}
          onTouchStart={stopCanvasInteractionPropagation}
        >
          {renderTools('right')}
        </div>
      )}

      {typeof document !== 'undefined' && drawerState.open && drawerPosition && createPortal(
        <div
          className="fixed z-50 animate-in fade-in zoom-in duration-150"
          data-drawing-controls="true"
          style={drawerPosition as React.CSSProperties}
          onMouseDown={stopCanvasInteractionPropagation}
          onTouchStart={stopCanvasInteractionPropagation}
        >
          <div
            ref={drawerRef}
            className="rounded-lg border border-white/10 bg-[#333333] shadow-[0_12px_30px_rgba(0,0,0,0.5)] px-1 py-2"
            style={{ width: drawerState.kind === 'drawing' || drawerState.kind === 'maskPen' ? '160px' : '88px' }}
            onMouseDown={stopCanvasInteractionPropagation}
            onTouchStart={stopCanvasInteractionPropagation}
          >
            {drawerState.kind === 'drawing' ? (
              drawingDrawer
            ) : drawerState.kind === 'maskPen' ? (
              maskPenDrawer
            ) : (
              <div className="space-y-2">
                {colorPicker}
                <div className="flex flex-col items-center gap-3">
                  <div
                    ref={sizeTrackRef}
                    className="relative flex items-center justify-center h-36 w-14 cursor-pointer select-none rounded-[18px] border border-white/10 bg-white/[0.04] px-5 py-3"
                    onPointerDown={handleSizePointerDown}
                    onPointerMove={handleSizePointerMove}
                    onPointerUp={handleSizePointerEnd}
                    onPointerCancel={handleSizePointerCancel}
                  >
                    <div className="absolute inset-y-3 w-[2px] rounded-full bg-gradient-to-b from-white/70 via-white/25 to-transparent" />
                    <div className="absolute inset-y-4 -left--3 flex flex-col justify-between">
                      {SIZE_TRACK_TICKS}
                    </div>
                    <div
                      className="absolute pointer-events-none rounded-full border border-white/70 shadow-[0_5px_14px_rgba(0,0,0,0.55),0_0_14px_currentColor] transition-transform duration-100"
                      style={{
                        width: 18,
                        height: 18,
                        bottom: `calc(${(annotationBrushSize / 96) * 100}% - 10px)`,
                        backgroundColor: annotationColor,
                        color: annotationColor,
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-mono font-semibold text-white/90">
                    {annotationBrushSize}px
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
});

NewInpaintToolbar.displayName = 'NewInpaintToolbar';
export default NewInpaintToolbar;
